import type React from "react";
import { createContext, useContext, useState } from "react";
import type { DocumentValidationIssue } from "../types/documentTypes";

interface DocumentDetails{
    fileName: string,
    mimeType: string,
    size: number,
    url: string,
    validationIssues?: DocumentValidationIssue[],
    focusedIssueIndex?: number,
}

export interface DocumentContextType {
    currentDocument: DocumentDetails | null;
    isOpen: boolean;
    isLoading: boolean;
    openDocument: (document: DocumentDetails) => void;
    closeDocument: () => void;
    setLoading: (loading: boolean) => void;
}

export const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const useDocumentContext = (): DocumentContextType => {
    const context = useContext(DocumentContext);
    if (!context) {
        throw new Error("useDocumentContext must be used within a DocumentProvider");
    }
    return context;
};

interface DocumentProviderProps {
    children: React.ReactNode;
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({ children }) => {
    const [currentDocument, setCurrentDocument] = useState<DocumentDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const openDocument = (document: DocumentDetails) => {
        console.log("open document:", document.fileName);
        setCurrentDocument(document);
        setIsLoading(true);
    };

    const closeDocument = () => {
        setCurrentDocument(null);
        setIsLoading(false);
    };

    const setLoading = (loading: boolean) => {
        setIsLoading(loading);
    };

    const value: DocumentContextType = {
        currentDocument,
        isOpen: currentDocument !== null,
        isLoading,
        openDocument,
        closeDocument,
        setLoading,
    };

    return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};
